"use client";
import { BlockTitle } from "@/app/_blocks/BlockTitle";
import { captureEvent } from "@/app/_analytics/analytics";
import { appUrls } from "@/constants";
import { EventName } from "@mw/shared/analyticsEventNames";
import type { ICmsLink } from "@mw/shared/payload-types";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconPlaylist from "@ui/central-icons/icons/IconPlaylist";
import IconTranslate from "@ui/central-icons/icons/IconTranslate";
import { TabList } from "@ui/components/core/TabList";
import clsx from "clsx";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Tabs, type Key } from "react-aria-components";

import { NativeAudioProvider } from "@/app/_providers/native-audio";
import type { AlbumCover, AlbumCoverVisualProps } from "./AlbumCoverTypes";
import { HERO_MUSIC_VISUAL_FRAME_CLASS_NAME, HERO_MUSIC_VISUAL_LAYOUT_STYLE } from "./HeroMusicVisualControls";
import { getWrappedIndex } from "./HeroMusicUtils";
import heroMusicDataJson from "./hero-music-covers.json";
import { HERO_MUSIC_TIMELINE_GROUP_IDS, type HeroMusicTimelineGroupId } from "./HeroMusicTimelineBar";
import { useHeroMusicPlayback } from "./useHeroMusicPlayback";

const AlbumCoverVisual = dynamic<AlbumCoverVisualProps>(() =>
  import("./AlbumCoverPlayer").then(mod => ({ default: mod.AlbumCoverVisual })),
);

const HERO_MUSIC_COVERS: AlbumCover[] = heroMusicDataJson.covers;

type HeroMusicV2Props = {
  title?: string | null;
  preTitle?: string | null;
  description?: string | null;
  ctaLink?: ICmsLink | null;
  secondaryCtaLink?: ICmsLink | null;
  covers?: AlbumCover[] | null;
};

export function HeroMusicV2(props: HeroMusicV2Props) {
  return (
    <NativeAudioProvider>
      <HeroMusicV2Inner {...props} />
    </NativeAudioProvider>
  );
}

function HeroMusicV2Inner({ title, preTitle, description, ctaLink, secondaryCtaLink, covers }: HeroMusicV2Props) {
  const t = useTranslations("ClientKeys.music.hero");
  const tGlobal = useTranslations("ClientKeys.Globals");
  const [activeCoverIndex, setActiveCoverIndex] = useState(0);
  const [activeTimelineGroup, setActiveTimelineGroup] = useState<HeroMusicTimelineGroupId>("genres");
  const heroMusicCovers = covers ?? HERO_MUSIC_COVERS;
  const { playingCoverIndex, playCover, toggleCover, seekCover, getCoverProgress, switchTimelineGroup, stopPlayback } =
    useHeroMusicPlayback(heroMusicCovers, activeTimelineGroup);
  const primaryLink =
    ctaLink ??
    ({
      type: "custom",
      url: appUrls.music,
      label: t("get_started"),
      appearance: "primary",
    } satisfies ICmsLink);
  const tabIconClassName = clsx(iconStrokeFix, "tw-size-3.5 tw-shrink-0");
  const musicTabs = HERO_MUSIC_TIMELINE_GROUP_IDS.map(id => ({
    id,
    title: t(id),
    icon:
      id === "genres" ? <IconPlaylist className={tabIconClassName} /> : <IconTranslate className={tabIconClassName} />,
  }));

  function handleActiveCoverIndexChange(index: number, previousIndex: number) {
    const nextCoverIndex = getWrappedIndex(index, heroMusicCovers.length);
    const previousCoverIndex = getWrappedIndex(previousIndex, heroMusicCovers.length);

    if (nextCoverIndex === previousCoverIndex) return;

    setActiveCoverIndex(nextCoverIndex);

    if (playingCoverIndex === previousCoverIndex) {
      void playCover(nextCoverIndex, 0);
      return;
    }

    stopPlayback();
    seekCover(nextCoverIndex, 0);
  }

  function togglePlay(coverIndex: number, options: { restart?: boolean } = {}) {
    if (options.restart) {
      void playCover(coverIndex, 0);
      return;
    }

    toggleCover(coverIndex);
  }

  function handleMusicTabChange(key: Key) {
    const nextTimelineGroup = key === "languages" ? "languages" : "genres";

    if (nextTimelineGroup === activeTimelineGroup) return;

    switchTimelineGroup(nextTimelineGroup);
    setActiveTimelineGroup(nextTimelineGroup);
    captureEvent(EventName.mw_music_hero_tab_changed, {
      tab_id: nextTimelineGroup,
    });
  }

  if (heroMusicCovers.length === 0) return null;

  return (
    <div className='tw-flex tw-min-h-fit tw-w-full tw-flex-col tw-items-center tw-justify-center'>
      <div
        className={clsx("tw-relative -tw-mx-full-bleed tw-self-stretch", HERO_MUSIC_VISUAL_FRAME_CLASS_NAME)}
        style={HERO_MUSIC_VISUAL_LAYOUT_STYLE}
      >
        <AlbumCoverVisual
          activeCoverIndex={activeCoverIndex}
          activeTimelineGroup={activeTimelineGroup}
          covers={heroMusicCovers}
          getPlaybackProgress={getCoverProgress}
          heightMode='flexible'
          nextLabel={tGlobal("next")}
          onActiveCoverIndexChange={handleActiveCoverIndexChange}
          onSeekPlaybackProgress={seekCover}
          onTogglePlay={togglePlay}
          pauseLabel={tGlobal("pause")}
          playLabel={tGlobal("play")}
          playingCoverIndex={playingCoverIndex}
          previousLabel={tGlobal("previous")}
        />
      </div>
      <div className='tw-container tw-flex tw-flex-col tw-items-center'>
        <Tabs
          className='tw-relative tw-z-40 -tw-mt-6 tw-w-full'
          onSelectionChange={handleMusicTabChange}
          selectedKey={activeTimelineGroup}
        >
          <TabList aria-label={t("music_tabs_aria_label")} items={musicTabs} centered />
        </Tabs>
        <BlockTitle
          buttonPosition='left'
          className='tw-w-full tw-pt-20 tw-pb-6'
          layout='two-column-hero'
          preTitleComplex={{ text: preTitle || t("hero_pretitle") }}
          primaryLink={primaryLink}
          secondaryLink={secondaryCtaLink}
          titleComplex={{
            text: title || t("hero_title"),
            options: {
              size: "large",
              tag: "h1",
            },
          }}
          titleWidth='wide'
          subtitleComplex={{
            text: description || t("hero_description"),
            options: {
              tag: "p",
            },
          }}
          topPadding='none'
        />
      </div>
    </div>
  );
}
